import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  getCurrentUser,
  getUserSeasons,
  createSeason as createSeasonQuery,
  getSeasonGames,
  addGame as addGameQuery,
  submitPicks,
  getUserPicks,
  getAllPicksToGrade,
  gradePick as gradePickQuery,
  getSeasonLeaderboard,
  getSeasonMembers,
  addFeaturedPlayers,
  getUniquePicksToGrade,
  gradeSelectionBulk
} from "../lib/supabaseQueries";

export default function DashboardPage() {
  const navigate = useNavigate();
  
  // Auth & User State
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Data State
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [games, setGames] = useState([]);
  const [featuredPlayers, setFeaturedPlayers] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [picksToGrade, setPicksToGrade] = useState([]);
  const [seasonMembers, setSeasonMembers] = useState([]);
  const [viewingWeek, setViewingWeek] = useState(1);
// Add these to your "Data State" section
  const [userMatchup, setUserMatchup] = useState(null); // The specific H2H for the current user
  const [allMatchups, setAllMatchups] = useState([]); // All pairings for the week (for the league view)  
  // UI State
  const [currentView, setCurrentView] = useState("seasons");
  const [currentTab, setCurrentTab] = useState("picks");
  const [leaderboardTab, setLeaderboardTab] = useState("combined");
  const [showMakePickModal, setShowMakePickModal] = useState(false);
  const [showAddGameModal, setShowAddGameModal] = useState(false);
  const [showGradePicksModal, setShowGradePicksModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinSeasonModal, setShowJoinSeasonModal] = useState(false);
  const [joinSeasonCode, setJoinSeasonCode] = useState("");
  const [joinSeasonError, setJoinSeasonError] = useState("");
  const [joiningSeasonLoading, setJoiningSeasonLoading] = useState(false);
  const [helpTab, setHelpTab] = useState('commissioner');
  // Calculated State
  const [currentWeek, setCurrentWeek] = useState(1);
  const isCommissioner = selectedSeason?.commissioner_id === currentUser?.id;
  const isDemo = localStorage.getItem("isDemo") === "true";
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // ... keep all your other state declarations
  // Pick selections for the modal (3 teams + 2 players)
  const [selections, setSelections] = useState({
    teamWinners: [],
    topPerformers: [],
  });

const [showAddPlayersModal, setShowAddPlayersModal] = useState(false);
const [addPlayersData, setAddPlayersData] = useState({
  gameId: "",
  players: [
    { name: "", team: "", position: "" },
    { name: "", team: "", position: "" }
  ]
});


  const [createSeasonData, setCreateSeasonData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    scoringType: "combined",
    pickFrequency: 5,
    startingBankroll: 1000,
    sports: ["NFL", "NBA", "NHL"],
    isPrivateMoneyPool: false,
  });

const [addGameData, setAddGameData] = useState({
    sport: "NFL",
    home_team: "",
    away_team: "",
    gameDate: "",
    gameTime: "",
    player1Name: "",
    player1Position: "",
    player2Name: "",
    player2Position: "",
  });

  
  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  // Load seasons when user changes
  useEffect(() => {
    if (currentUser) {
      loadSeasons();
    }
  }, [currentUser]);

  // Load data when season changes
useEffect(() => {
  if (selectedSeason && currentUser) {
    const week = calculateCurrentWeek(selectedSeason.start_date);
    setCurrentWeek(week);
    setViewingWeek(week); // Default to the "live" week
    loadSeasonData(week);
  }
}, [selectedSeason, currentUser]);

  // Auto-open Make Picks modal when entering a season (KEEP THIS - already exists)
// useEffect(() => {
//   if (currentView === "season" && selectedSeason) {
//     setShowMakePickModal(true);
//   }
// }, [currentView, selectedSeason]);

  // const isCommissioner = selectedSeason?.commissioner_id === currentUser?.id;
  
// const formatETDate = (dateString) => {
//   const date = new Date(dateString);
//   return new Intl.DateTimeFormat("en-US", {
//     timeZone: "America/New_York",
//     month: "short",
//     day: "numeric",
//   }).format(date);
// };
// OPTION 1: Update loadUser to CREATE profile if missing
// Replace your loadUser function with this:

const loadUser = async () => {
  try {
    // Check for active session first
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (!session) {
      console.log('No active session - redirecting to login');
      setLoading(false);
      navigate('/login');
      return;
    }

    if (sessionError) {
      console.error('Session error:', sessionError);
      setLoading(false);
      navigate('/login');
      return;
    }

    // Try to get the user profile
    let { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', session.user.id)
      .single();
    
    // If profile doesn't exist, create it
    if (profileError && profileError.code === 'PGRST116') {
      console.log('Profile not found, creating one...');
      
      // Extract username from metadata or use email prefix
      const username = session.user.user_metadata?.username || 
                      session.user.email.split('@')[0];
      
      // Create the profile
      const { data: newProfile, error: createError } = await supabase
        .from('users')
        .insert([{
          id: session.user.id,
          username: username,
          email: session.user.email
        }])
        .select()
        .single();
      
      if (createError) {
        console.error('Error creating profile:', createError);
        setLoading(false);
        navigate('/login');
        return;
      }
      
      profile = newProfile;
      console.log('✅ Profile created successfully!', profile);
    } else if (profileError) {
      console.error('Error loading profile:', profileError);
      setLoading(false);
      navigate('/login');
      return;
    }

    // Success! Set the user
    setCurrentUser(profile);
    setLoading(false);
  } catch (error) {
    console.error('Unexpected error loading user:', error);
    setLoading(false);
    navigate('/login');
  }
};



  const loadSeasons = async () => {
    try {
      const data = await getUserSeasons(currentUser.id);
      setSeasons(data || []);
    } catch (error) {
      console.error('Error loading seasons:', error);
      setSeasons([]);
    }
  };

// COMPLETE loadSeasonData WITH MATCHUP FEATURE
// ==============================================

const loadSeasonData = async (weekNumber) => {
  // 1. Determine the week. Priority: Passed argument -> State -> Default to 1
  const week = weekNumber || currentWeek || 1; 

  if (!selectedSeason?.id || !currentUser?.id) return;

  try {
    // 2. Pass the 'week' to getSeasonGames! 
    // This was previously missing the second argument.
    const gamesData = await getSeasonGames(selectedSeason.id, week);
    setGames(gamesData || []);

    const allPlayers = gamesData?.flatMap(game => game.featured_players || []) || [];
    setFeaturedPlayers(allPlayers);

    // 3. Load user's picks for this specific week
    const picksData = await getUserPicks(
      currentUser.id,
      selectedSeason.id,
      week
    );
    setMyPicks(picksData || []);

    // 4. Load leaderboard (Season-wide)
    const leaderboardData = await getSeasonLeaderboard(selectedSeason.id);
    setLeaderboard(leaderboardData || []);

    const membersData = await getSeasonMembers(selectedSeason.id);
    setSeasonMembers(membersData || []);

    // 5. Use the safe 'week' variable for grading
    if (isCommissioner) {
      const toGrade = await getAllPicksToGrade(selectedSeason.id, week);
      setPicksToGrade(toGrade || []);
    }

    // 6. NEW: Load current user's matchup for the viewing week
    const { data: matchup, error: matchupError } = await supabase
      .from('matchups')
      .select(`
        id,
        week_number,
        user1_id,
        user2_id,
        user1_score,
        user2_score,
        winner_id,
        is_completed,
        user1:users!user1_id (username, id),
        user2:users!user2_id (username, id)
      `)
      .eq('season_id', selectedSeason.id)
      .eq('week_number', week)
      .or(`user1_id.eq.${currentUser.id},user2_id.eq.${currentUser.id}`)
      .maybeSingle();

    if (!matchupError && matchup) {
      // Determine if current user is user1 or user2
      const isUser1 = matchup.user1_id === currentUser.id;
      
      // Fetch both users' picks for the matchup
      const { data: allMatchupPicks } = await supabase
        .from('picks')
        .select(`
          id,
          user_id,
          pick_type,
          selection,
          status,
          fantasy_score,
          is_boosted,
          games (sport, home_team, away_team),
          featured_players (name, team, sport)
        `)
        .eq('season_id', selectedSeason.id)
        .eq('week_number', week)
        .in('user_id', [matchup.user1_id, matchup.user2_id]);

      const myMatchupPicks = allMatchupPicks?.filter(p => p.user_id === currentUser.id) || [];
      const opponentId = isUser1 ? matchup.user2_id : matchup.user1_id;
      const opponentPicks = allMatchupPicks?.filter(p => p.user_id === opponentId) || [];

      setUserMatchup({
        id: matchup.id,
        weekNumber: matchup.week_number,
        myScore: isUser1 ? matchup.user1_score : matchup.user2_score,
        opponentScore: isUser1 ? matchup.user2_score : matchup.user1_score,
        opponentName: isUser1 ? matchup.user2.username : matchup.user1.username,
        opponentId: opponentId,
        winnerId: matchup.winner_id,
        isCompleted: matchup.is_completed,
        myPicks: myMatchupPicks,
        opponentPicks: opponentPicks
      });
    } else {
      setUserMatchup(null);
    }

  } catch (error) {
    console.error('Error loading season data:', error);
  }
};

  const calculateCurrentWeek = (seasonStartDate) => {
    const start = new Date(seasonStartDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    return Math.max(1, diffWeeks);
  };

const handleJoinSeason = async (e) => {
  e.preventDefault();
  setJoinSeasonError("");
  setJoiningSeasonLoading(true);

  try {
    // Clean up the invite code (remove spaces, make uppercase)
    const cleanCode = joinSeasonCode.trim().toUpperCase();

    if (cleanCode.length !== 9) {
      setJoinSeasonError("Invite code must be 9 characters");
      setJoiningSeasonLoading(false);
      return;
    }

    // Find the season by invite code
    const { data: season, error: seasonError } = await supabase
      .from('seasons')
      .select('*')
      .eq('invite_code', cleanCode)
      .single();

    if (seasonError || !season) {
      setJoinSeasonError("Invalid invite code. Please check and try again.");
      setJoiningSeasonLoading(false);
      return;
    }

    // Check if user is already a member
    const { data: existingMember } = await supabase
      .from('season_members')
      .select('*')
      .eq('season_id', season.id)
      .eq('user_id', currentUser.id)
      .single();

    if (existingMember) {
      setJoinSeasonError("You're already a member of this season!");
      setJoiningSeasonLoading(false);
      return;
    }

    // Add user as a member with starting bankroll
    const { error: joinError } = await supabase
      .from('season_members')
      .insert([{
        season_id: season.id,
        user_id: currentUser.id,
        bankroll: season.starting_bankroll
      }]);

    if (joinError) throw joinError;

    // Success!
    alert(`✅ Joined "${season.name}" successfully!`);
    
    // Reset form
    setJoinSeasonCode("");
    setShowJoinSeasonModal(false);
    
    // Reload seasons list
    await loadSeasons();
  } catch (error) {
    console.error('Error joining season:', error);
    setJoinSeasonError("Failed to join season. Please try again.");
  } finally {
    setJoiningSeasonLoading(false);
  }
};

const formatGameTime = (dateString) => {
  if (!dateString) return "";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dateString));
};

const formatETDate  = (dateString) => {
  if (!dateString || typeof dateString !== "string") return "";

  const datePart = dateString.slice(0, 10);
  const parts = datePart.split("-");
  if (parts.length !== 3) return "";

  const [, month, day] = parts;
  return `${month}/${day}`;
};

const generateSeasonSchedule = async (seasonId) => {
  try {
    // 1. Get Season details for duration calculation
    const { data: season, error: seasonError } = await supabase
      .from('seasons')
      .select('start_date, end_date')
      .eq('id', seasonId)
      .single();

    if (seasonError) throw seasonError;

    // 2. SAFETY CHECK: Ensure no points have been scored yet
    const { data: activeMatchups, error: checkError } = await supabase
      .from('matchups')
      .select('user1_score, user2_score')
      .eq('season_id', seasonId)
      .or('user1_score.gt.0,user2_score.gt.0')
      .limit(1);

    if (checkError) throw checkError;

    if (activeMatchups && activeMatchups.length > 0) {
      throw new Error("CANNOT REGENERATE: Games have already been graded for this season.");
    }

    // 3. WIPE EXISTING SCHEDULE: Clear empty matchups for a fresh start
    const { error: deleteError } = await supabase
      .from('matchups')
      .delete()
      .eq('season_id', seasonId);
      
    if (deleteError) throw deleteError;

    // 4. CALCULATE TOTAL WEEKS
    const start = new Date(season.start_date);
    const end = new Date(season.end_date);
    const totalWeeks = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24 * 7));

    // 5. FETCH ALL MEMBERS
    const { data: members, error: memError } = await supabase
      .from('season_members')
      .select('user_id')
      .eq('season_id', seasonId);

    if (memError) throw memError;
    if (members.length < 2) throw new Error("Need at least 2 members to play.");

    const playerIds = members.map(m => m.user_id);
    
    // Add a BYE if player count is odd
    if (playerIds.length % 2 !== 0) playerIds.push(null); 

    const numPlayers = playerIds.length;
    const matchupsToCreate = [];

    // 6. ROUND ROBIN ALGORITHM
    // Each week, players are paired and then the list "rotates"
    for (let week = 1; week <= totalWeeks; week++) {
      for (let i = 0; i < numPlayers / 2; i++) {
        const p1 = playerIds[i];
        const p2 = playerIds[numPlayers - 1 - i];

        if (p1 !== null && p2 !== null) {
          matchupsToCreate.push({
            season_id: seasonId,
            week_number: week,
            user1_id: p1,
            user2_id: p2,
            user1_score: 0,
            user2_score: 0
          });
        }
      }
      // Rotate array (keep index 0 fixed)
      playerIds.splice(1, 0, playerIds.pop());
    }

    // 7. BATCH INSERT
    const { error: insertError } = await supabase
      .from('matchups')
      .insert(matchupsToCreate);

    if (insertError) throw insertError;

    return { success: true, weeksGenerated: totalWeeks, matchupCount: matchupsToCreate.length };

  } catch (error) {
    console.error("Schedule Generation Failed:", error.message);
    throw error;
  }
};

  // Auto-open Make Picks modal when entering a season
  // useEffect(() => {
  //   if (currentView === "season" && selectedSeason) {
  //     // Auto-open the picks modal
  //     setShowMakePickModal(true);
  //   }
  // }, [currentView, selectedSeason]);

  const handleSelectSeason = (season) => {
    setSelectedSeason(season);
    setCurrentView("season");
    setCurrentTab("picks");
  };

const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      const newSeason = await createSeasonQuery(createSeasonData, currentUser.id);
      alert(`✅ Season created! Invite code: ${newSeason.invite_code}`);
      
      setCreateSeasonData({
        name: "",
        startDate: "",
        endDate: "",
        scoringType: "combined",
        pickFrequency: 5,
        startingBankroll: 1000,
        sports: ["NFL", "NBA", "NHL"],
        isPrivateMoneyPool: false,
      });
      
      await loadSeasons();
      setCurrentView("seasons");
    } catch (error) {
      console.error('Error creating season:', error);
      alert(`Error creating season: ${error.message}`);
    }
  };

const handleAddGame = async (e) => {
  e.preventDefault();
  try {
    // Calculate which week this specific game belongs to
    // using the date selected in the form
    const gameDateObj = new Date(addGameData.gameDate);
    const seasonStart = new Date(selectedSeason.start_date);
    
    // Formula: (Game Date - Season Start) / Days in Week
    const diffTime = Math.abs(gameDateObj - seasonStart);
    const gameWeek = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7)) || 1;

    // Pass 'gameWeek' instead of 'currentWeek'
    const game = await addGameQuery(addGameData, selectedSeason.id, gameWeek); 
      // Add featured players if provided
      const players = [];
      if (addGameData.player1Name) {
        players.push({
          name: addGameData.player1Name,
          team: addGameData.home_team,
          sport: addGameData.sport,
          position: addGameData.player1Position
        });
      }
      if (addGameData.player2Name) {
        players.push({
          name: addGameData.player2Name,
          team: addGameData.away_team,
          sport: addGameData.sport,
          position: addGameData.player2Position
        });
      }
      
      if (players.length > 0) {
        await addFeaturedPlayers(game.id, players);
      }
      
      alert("✅ Game and players added successfully!");
      
      setAddGameData({
        sport: "NFL",
        home_team: "",
        away_team: "",
        gameDate: "",
        gameTime: "",
        player1Name: "",
        player1Position: "",
        player2Name: "",
        player2Position: "",
      });
      setShowAddGameModal(false);
      
      await loadSeasonData();
    } catch (error) {
      console.error('Error adding game:', error);
      alert(`Error adding game: ${error.message}`);
    }
  };

const handleAddFeaturedPlayers = async (e) => {
  e.preventDefault();
  try {
    // Filter out empty players
    const validPlayers = addPlayersData.players.filter(
      p => p.name.trim() !== ""
    );

    if (validPlayers.length === 0) {
      alert("Please add at least one player");
      return;
    }

    // Find the game to get sport info
    const game = games.find(g => g.id === addPlayersData.gameId);
    if (!game) {
      alert("Game not found");
      return;
    }

    // Add sport to each player
    const playersWithSport = validPlayers.map(p => ({
      ...p,
      sport: game.sport
    }));

    await addFeaturedPlayers(addPlayersData.gameId, playersWithSport);
    
    alert(`✅ Added ${validPlayers.length} featured player(s)!`);
    
    // Reset form
    setAddPlayersData({
      gameId: "",
      players: [
        { name: "", team: "", position: "" },
        { name: "", team: "", position: "" }
      ]
    });
    setShowAddPlayersModal(false);
    
    await loadSeasonData();
  } catch (error) {
    console.error('Error adding featured players:', error);
    alert(`Error: ${error.message}`);
  }
};

const addPlayerRow = () => {
  setAddPlayersData({
    ...addPlayersData,
    players: [
      ...addPlayersData.players,
      { name: "", team: "", position: "" }
    ]
  });
};

const removePlayerRow = (index) => {
  setAddPlayersData({
    ...addPlayersData,
    players: addPlayersData.players.filter((_, i) => i !== index)
  });
};

const updatePlayer = (index, field, value) => {
  const newPlayers = [...addPlayersData.players];
  newPlayers[index][field] = value;
  setAddPlayersData({
    ...addPlayersData,
    players: newPlayers
  });
};

// 1. Function to refresh the list of unique picks
const refreshPicksToGrade = async () => {
  if (!selectedSeason) return;
  try {
    const uniquePicks = await getUniquePicksToGrade(selectedSeason.id, currentWeek);
    setPicksToGrade(uniquePicks || []);
  } catch (error) {
    console.error("Failed to fetch picks:", error);
  }
};

// 2. The Bulk Grading Handler
const handleGradeSelection = async (selection, score) => {
  try {
    // Perform the bulk update in Supabase
    await gradeSelectionBulk(selectedSeason.id, currentWeek, selection, score);
    
    // Optimistic UI update: remove the selection from the list immediately
    setPicksToGrade(prev => prev.filter(item => item.selection !== selection));
    
    // Optional: Reload leaderboard in background so it's fresh when modal closes
    const updatedLeaderboard = await getSeasonLeaderboard(selectedSeason.id);
    setLeaderboard(updatedLeaderboard || []);
    
    console.log(`Successfully graded ${selection} for all users.`);
  } catch (err) {
    console.error("Grading failed:", err);
    alert("Error updating picks. Check console.");
  }
};

const handleGradePick = async (pickId, score) => {
  try {
    console.log('🎯 Grading pick:', { pickId, score });
    
    await gradePickQuery(pickId, score);
    
    const scoreNum = parseInt(score);
    const isCorrect = scoreNum >= 100;
    
    alert(`✅ Pick graded with ${score} points ${isCorrect ? '(Correct ✓)' : '(Incorrect ✗)'}`);
    
    // Reload data to refresh the modal and picks
    await loadSeasonData(currentWeek);
  } catch (error) {
    console.error('Error grading pick:', error);
    alert(`Error grading pick: ${error.message}`);
  }
};

  const toggleSport = (sport) => {
    if (createSeasonData.sports.includes(sport)) {
      setCreateSeasonData({
        ...createSeasonData,
        sports: createSeasonData.sports.filter((s) => s !== sport),
      });
    } else {
      setCreateSeasonData({
        ...createSeasonData,
        sports: [...createSeasonData.sports, sport],
      });
    }
  };

  // Toggle team winner edion
  const toggleTeamWinner = (gameId, team, sport) => {
    const existing = selections.teamWinners.find((w) => w.gameId === gameId);

    if (existing) {
      setSelections({
        ...selections,
        teamWinners: selections.teamWinners.filter((w) => w.gameId !== gameId),
      });
    } else {
      if (selections.teamWinners.length >= 3) {
        alert("You can only select 3 team winners");
        return;
      }

      const sameSportCount = selections.teamWinners.filter(
        (w) => w.sport === sport
      ).length;

      if (sameSportCount >= 2) {
        alert(`You can only select 2 games from ${sport}`);
        return;
      }

      setSelections({
        ...selections,
        teamWinners: [...selections.teamWinners, { gameId, team, sport }],
      });
    }
  };

  // Toggle top performer selection
const toggleTopPerformer = (playerId, name, team, sport, gameId) => {
  setSelections((prev) => {
    const isSelected = prev.topPerformers.some((p) => p.playerId === playerId);

    if (isSelected) {
      // Remove player and clear boost if that player was boosted
      return {
        ...prev,
        topPerformers: prev.topPerformers.filter((p) => p.playerId !== playerId),
        boostedPlayerId: prev.boostedPlayerId === playerId ? null : prev.boostedPlayerId,
      };
    } else {
      // Only allow adding if we have less than 2 players
      if (prev.topPerformers.length < 2) {
        return {
          ...prev,
          topPerformers: [
            ...prev.topPerformers, 
            { playerId, name, team, sport, gameId } // <--- gameId added here
          ],
        };
      }
      return prev;
    }
  });
};

  // Check if team is selected
  const isTeamSelected = (gameId, team) => {
    return selections.teamWinners.some(
      (w) => w.gameId === gameId && w.team === team
    );
  };

  // Check if player is selected
  const isPlayerSelected = (playerId) => {
    return selections.topPerformers.some((p) => p.playerId === playerId);
  };

  // Check if can submit
  const canSubmit = () => {
    return (
      selections.teamWinners.length === 3 &&
      selections.topPerformers.length === 2
    );
  };

  // Handle picks submission
const handleSubmitPicks = async (e) => {
  e.preventDefault();
  
  // Basic validation
  if (selections.teamWinners.length !== 3 || selections.topPerformers.length !== 2) {
    alert("Please select exactly 3 teams and 2 players.");
    return;
  }

  try {
    // We pass 'upcomingGames' so the query can check kickoff times
    await submitPicks(
      currentUser.id, 
      selectedSeason.id, 
      viewingWeek, 
      selections, 
      upcomingGames 
    );

    alert("✅ Picks updated successfully!");
    setShowMakePickModal(false);
    await loadSeasonData(viewingWeek); // Refresh UI
  } catch (error) {
    console.error("Submission error:", error);
    alert(error.message);
  }
};

function PickDeadline({ games }) {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const nextGame = games
      .filter(g => new Date(g.game_time) > new Date())
      .sort((a, b) => new Date(a.game_time) - new Date(b.game_time))[0];
    
    if (!nextGame) return;
    
    const timer = setInterval(() => {
      const diff = new Date(nextGame.game_time) - new Date();
      if (diff <= 0) { setTimeLeft('LOCKED'); clearInterval(timer); return; }
      
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [games]);

  if (!timeLeft) return null;
  
  return (
    <div className={`text-white rounded-2xl p-4 mb-6 flex items-center justify-between ${
      timeLeft === 'LOCKED' 
        ? 'bg-gradient-to-r from-gray-600 to-gray-700'
        : 'bg-gradient-to-r from-red-500 to-orange-500'
    }`}>
      <div>
        <p className="text-xs font-black uppercase tracking-wider opacity-80">
          {timeLeft === 'LOCKED' ? 'Picks Locked' : 'First Game In'}
        </p>
        <p className="text-3xl font-black">{timeLeft}</p>
      </div>
      <p className="text-4xl">{timeLeft === 'LOCKED' ? '🔒' : '⏰'}</p>
    </div>
  );
}

const totalWeeks = selectedSeason 
  ? Math.ceil(
      Math.abs(new Date(selectedSeason.end_date) - new Date(selectedSeason.start_date)) 
      / (1000 * 60 * 60 * 24 * 7)
    )
  : 1;

  const sportColors = {
    NFL: "from-red-500 to-orange-500",
    NBA: "from-blue-500 to-indigo-500",
    NHL: "from-cyan-500 to-blue-500",
    MLB: "from-green-500 to-emerald-500",
  };

  // Get only upcoming games for picks modal
  const upcomingGames = games.filter((g) => g.status === "upcoming");

const totalPicksAllowed = 5; // Or whatever your league limit is
const picksMade = myPicks.length;
const picksGraded = myPicks.filter(p => p.status === 'graded').length;
const pendingPicks = myPicks.filter(p => p.status === 'pending').length;
const totalWeeklyPoints = myPicks.reduce((sum, p) => sum + (parseInt(p.fantasy_score) || 0), 0);

  // CREATE SEASON VIEW
  if (currentView === "createSeason") {
    return (
      <div className="min-h-screen bg-gray-50">
<div className="gradient-bg text-white shadow-xl sticky top-0 z-50">
  <div className="max-w-7xl mx-auto px-4 py-4">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight"></h1>
        <p className="text-sm text-emerald-200 mt-0.5">
          Create New Season
        </p>
      </div>
      <div className="flex gap-3">
        {/* <button
          onClick={() => setCurrentTab('manage')}
          className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur transition flex items-center justify-center font-black text-lg"
        >
          ?
        </button> */}
        <button
          onClick={() => setCurrentView("seasons")}
          className="px-5 py-2.5 rounded-xl font-semibold transition bg-white text-emerald-700 shadow-lg"
        >
          ← Cancel
        </button>
      </div>
    </div>
  </div>
</div>

        <div className="max-w-3xl mx-auto px-4 py-8 pb-24 animate-slide-in">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Create New Season
            </h2>
            <p className="text-gray-500">Set up your competition parameters</p>
          </div>

          <form
            onSubmit={handleCreateSeason}
            className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 space-y-7 border border-gray-100"
          >
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Season Name
              </label>
              <input
                type="text"
                placeholder="e.g., NFL Championship Season"
                value={createSeasonData.name}
                onChange={(e) =>
                  setCreateSeasonData({
                    ...createSeasonData,
                    name: e.target.value,
                  })
                }
                required
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-lg outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Start Date
                </label>
                <input
                  type="date"
                  value={createSeasonData.startDate}
                  onChange={(e) =>
                    setCreateSeasonData({
                      ...createSeasonData,
                      startDate: e.target.value,
                    })
                  }
                  required
                  className="w-full px-3 sm:px-5 py-3 sm:py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition outline-none text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  End Date
                </label>
                <input
                  type="date"
                  value={createSeasonData.endDate}
                  onChange={(e) =>
                    setCreateSeasonData({
                      ...createSeasonData,
                      endDate: e.target.value,
                    })
                  }
                  required
                  className="w-full px-3 sm:px-5 py-3 sm:py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition outline-none text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Scoring System
              </label>
              <select
                value={createSeasonData.scoringType}
                onChange={(e) =>
                  setCreateSeasonData({
                    ...createSeasonData,
                    scoringType: e.target.value,
                  })
                }
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-base font-medium outline-none"
              >
                <option value="combined">Points Based</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Weekly Pick Limit
              </label>
              <select
                value={createSeasonData.pickFrequency}
                onChange={(e) =>
                  setCreateSeasonData({
                    ...createSeasonData,
                    pickFrequency: parseInt(e.target.value),
                  })
                }
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-base font-medium outline-none"
              >
                <option value={5}>5 picks per week (3 teams + 2 players)</option>
              </select>
            </div>

            {/* <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Starting Bankroll (Virtual)
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[100, 500, 1000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() =>
                      setCreateSeasonData({
                        ...createSeasonData,
                        startingBankroll: amount,
                      })
                    }
                    className={`py-4 rounded-xl font-bold transition ${
                      createSeasonData.startingBankroll === amount
                        ? "bg-gradient-to-br from-emerald-600 to-green-600 text-white shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div> */}

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-4">
                Allowed Sports
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  "NFL",
                  "NBA",
                  "MLB",
                  "NHL",
                  "NCAA FB",
                  "NCAA BB",
                  "Soccer",
                  "UFC",
                ].map((sport) => (
                  <label
                    key={sport}
                    className={`flex items-center p-4 rounded-xl cursor-pointer transition ${
                      createSeasonData.sports.includes(sport)
                        ? "bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300"
                        : "bg-gray-50 border-2 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={createSeasonData.sports.includes(sport)}
                      onChange={() => toggleSport(sport)}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500"
                    />
                    <span className="ml-3 font-semibold text-gray-900">
                      {sport}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-2 border-yellow-400 rounded-xl p-5 bg-yellow-50">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={createSeasonData.isPrivateMoneyPool}
                  onChange={(e) =>
                    setCreateSeasonData({
                      ...createSeasonData,
                      isPrivateMoneyPool: e.target.checked,
                    })
                  }
                  className="w-5 h-5 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500 mt-0.5"
                />
                <div>
                  <span className="font-bold text-yellow-900 block mb-1">
                    💰 This is a private money pool
                  </span>
                  <p className="text-xs text-yellow-800">
                    Checking this box indicates participants have organized a
                    private money pool outside of  platform. 
                    does not collect, hold, or distribute any funds. All
                    financial arrangements are between participants only.
                  </p>
                </div>
              </label>
            </div>

            <button
              type="submit"
              className="w-full btn-primary text-white py-5 rounded-xl font-bold text-lg shadow-xl"
            >
              Create Season & Get Invite Code
            </button>
          </form>
        </div>
      </div>
    );
  }

  // SEASONS LIST VIEW
  if (currentView === "seasons") {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="gradient-bg text-white shadow-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">PickSlate</h1>
              </div>
              <div className="flex gap-3">
                <button className="px-5 py-2.5 rounded-xl font-semibold transition bg-white text-emerald-700 shadow-lg">
                  Seasons
                </button>
                <Link
                  to="/"
                  className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur transition flex items-center justify-center text-lg"
                >
                  ←
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 pb-24 animate-slide-in">
          {/* {isDemo && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6">
              <p className="font-bold text-yellow-900">🎮 Demo Mode</p>
              <p className="text-sm text-yellow-700">
                You're viewing demo data.
                <Link to="/signup" className="font-bold underline ml-1">
                  Sign up to create your own seasons
                </Link>
              </p>
            </div>
          )} */}

  <div className="flex justify-between items-center mb-8">
  <div>
    <h2 className="text-3xl font-bold text-gray-900">Your Seasons</h2>
    <p className="text-gray-500 mt-1">
      Manage and join prediction competitions
    </p>
  </div>
  <div className="flex gap-3">
<button
  onClick={() => setShowJoinSeasonModal(true)}
  className="bg-white border-2 border-emerald-600 text-emerald-600 px-6 py-3.5 rounded-xl font-bold shadow-lg hover:bg-emerald-50 transition"
>
  Join Season
</button>
<button
  onClick={() => setCurrentView("createSeason")}
  className="btn-primary text-white px-6 py-3.5 rounded-xl font-bold shadow-lg"
>
  + New Season
</button>
  </div>
</div>
          <div className="grid gap-5">
            {seasons.map((season) => (
              <div
                key={season.id}
                onClick={() => handleSelectSeason(season)}
                className="bg-white rounded-2xl shadow-lg p-7 cursor-pointer card-hover border border-gray-100"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900">
                        {season.name}
                      </h3>
                      {season.isActive && (
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                          • LIVE
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {season.commissioner === "You"
                        ? "You're the commissioner"
                        : `Commissioner: ${season.commissioner}`}
                    </p>
                    <div className="flex items-center gap-2">
                    <span className="text-lg">📅</span>
                    <span>
                      {formatETDate(season.start_date)} - {formatETDate(season.end_date)}
                    </span>
                  </div>  
                  </div>

                  <div className="sm:text-right">
                    <div className="bg-gradient-to-br from-emerald-100 to-green-100 text-emerald-700 px-4 py-2 rounded-xl text-sm font-bold inline-block">
                      {season.members} members
                    </div>
                  </div>
                </div>
                <div className="flex gap-6 text-sm text-gray-600 pt-4 border-t border-gray-100">
                  {/* <div className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="font-semibold">
                      {season.scoringType === "combined"
                        ? "Combined Scoring"
                        : season.scoringType === "roi"
                        ? "ROI Only"
                        : "Hit Rate Only"}
                    </span>
                  </div> */}
{season.currentMatchup && (
  <div className="mt-4 flex justify-between items-center bg-emerald-50 p-3 rounded-xl border border-emerald-100">
    <div className="text-xs font-bold text-emerald-800 uppercase whitespace-nowrap">
      Week {season.currentMatchup.week} H2H
    </div>
    <div className="flex items-center text-sm font-medium gap-2 ml-6">
      <span className="text-gray-900 font-bold">You ({season.currentMatchup.myScore})</span>
      <span className="text-gray-400 text-xs">vs</span>
      <span className="text-gray-900">
        {season.currentMatchup.opponentName} ({season.currentMatchup.opponentScore})
      </span>
    </div>
  </div>
)}
           
                </div>
              </div>
            ))}
          </div>
        </div>
{showJoinSeasonModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
      <div className="bg-gradient-to-br from-emerald-600 to-green-600 text-white p-6 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Join Season</h2>
          <button
            onClick={() => {
              setShowJoinSeasonModal(false);
              setJoinSeasonCode("");
              setJoinSeasonError("");
            }}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition"
          >
            ✕
          </button>
        </div>
        <p className="text-emerald-100 text-sm mt-1">
          Enter an invite code to join a season
        </p>
      </div>

      <form onSubmit={handleJoinSeason} className="p-7 space-y-6">
        {joinSeasonError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <p className="text-red-800 text-sm font-semibold">
              ⚠️ {joinSeasonError}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-bold text-gray-900 mb-3">
            Invite Code
          </label>
          <input
            type="text"
            placeholder="ABC123XYZ"
            value={joinSeasonCode}
            onChange={(e) => setJoinSeasonCode(e.target.value.toUpperCase())}
            maxLength={9}
            required
            disabled={joiningSeasonLoading}
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-2xl font-bold text-center tracking-widest uppercase outline-none disabled:opacity-50"
          />
          <p className="text-xs text-gray-500 mt-2 text-center">
            Enter the 9-character code shared by the season commissioner
          </p>
        </div>

        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <p className="text-sm text-emerald-800">
            💡 <strong>Don't have a code?</strong> Ask a friend who's running a season to share their invite code with you.
          </p>
        </div>

        <button
          type="submit"
          disabled={joiningSeasonLoading || joinSeasonCode.length !== 9}
          className="w-full bg-gradient-to-br from-emerald-600 to-green-600 text-white py-5 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {joiningSeasonLoading ? "Joining..." : "Join Season"}
        </button>
      </form>
    </div>
  </div>
)}        
      </div>
    );
  }

  // SEASON DETAIL VIEW (with tabs)
  return (
    <div className="min-h-screen bg-gray-50">
<div className="gradient-bg text-white shadow-xl sticky top-0 z-50">
  <div className="max-w-7xl mx-auto px-4 py-4">
    <div className="flex items-center justify-between">
      
      {/* Left - season name */}
      <div className="min-w-0 flex-1 mr-3">
        {selectedSeason && (
          <p className="text-sm text-emerald-200 truncate">{selectedSeason.name}</p>
        )}
      </div>

      {/* Center - logo */}
      <div className="flex flex-col items-center shrink-0">
        <img src="/PickSlateLogoIcon.png" alt="PickSlate" className="h-9 w-auto" />
      </div>

      {/* Right - buttons */}
      <div className="flex gap-2 shrink-0 flex-1 justify-end ml-3">
        <button
          onClick={() => setShowHelpModal(true)}
          className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur transition flex items-center justify-center font-black"
        >
          ?
        </button>
        <button
          onClick={() => setCurrentView("seasons")}
          className="px-3 sm:px-5 py-2.5 rounded-xl font-semibold transition bg-white text-emerald-700 shadow-lg text-sm sm:text-base"
        >
          <span className="sm:hidden">←</span>
          <span className="hidden sm:inline">← Seasons</span>
        </button>
      </div>

    </div>
  </div>
</div>

      <div className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {/* MY PICKS TAB */}
        
{currentTab === "picks" && (
  <div className="animate-slide-in">
{/* Week Selector */}
<div className="flex items-center gap-4 mb-6 bg-white p-2 rounded-2xl w-fit border-2 border-gray-100">
<button 
  onClick={() => {
    const prev = Math.max(1, viewingWeek - 1);
    setViewingWeek(prev);
    loadSeasonData(prev);
  }}
  disabled={viewingWeek === 1}
  className="p-2 hover:bg-gray-100 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed"
>
  ⬅️
</button>

<span className="font-black text-md">Week {viewingWeek} <span className="text-xs text-gray-400 font-normal">/ {totalWeeks}</span></span>

<button 
  onClick={() => {
    const next = Math.min(totalWeeks, viewingWeek + 1);
    setViewingWeek(next);
    loadSeasonData(next);
  }}
  disabled={viewingWeek === totalWeeks}
  className="p-2 hover:bg-gray-100 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed"
>
  ➡️
</button>
{myPicks.length < 6 && (
        <button
          onClick={() => setShowMakePickModal(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-emerald-200 transition-all active:scale-95"
        >
          {/* <span className="text-xl">🎯</span> */}
          Make Picks
        </button>
      )}
</div>    

    {viewingWeek === currentWeek && picksMade < totalPicksAllowed && (
      <PickDeadline games={games} />
    )}

<div className="flex items-center gap-3 mb-4 px-1">
  <div className="flex items-center gap-1.5 bg-white border-2 border-gray-100 rounded-xl px-3 py-1.5 shadow-sm">
    <span className="text-base">🎯</span>
    <span className="text-sm font-black text-gray-900">{picksMade}</span>
    <span className="text-sm text-gray-400 font-medium">/ {totalPicksAllowed}</span>
  </div>

  <div className="flex items-center gap-1.5 bg-emerald-50 border-2 border-emerald-100 rounded-xl px-3 py-1.5 shadow-sm">
    <span className="text-base">⚡</span>
    <span className="text-sm font-black text-emerald-700">{totalWeeklyPoints} pts</span>
  </div>

  <div className="flex items-center gap-1.5 bg-white border-2 border-gray-100 rounded-xl px-3 py-1.5 shadow-sm">
    <span className="text-base">✅</span>
    <span className="text-sm font-black text-gray-900">{picksGraded}</span>
    <span className="text-sm text-gray-400 font-medium">graded</span>
  </div>
</div>

    <div className="space-y-4">
      {myPicks.map((pick) => (
        <div
          key={pick.id}
          className={`bg-white rounded-2xl shadow-lg p-6 border-2 transition-all ${
            pick.is_boosted ? "border-yellow-400" : "border-gray-100"
          }`}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span
                  className={`px-2 py-1 rounded text-xs font-bold ${
                    pick.pick_type === "team_winner"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-purple-100 text-purple-700"
                  }`}
                >
                  {pick.pick_type === "team_winner"
                    ? "🏆 Team Winner"
                    : "⭐ Top Performer"}
                </span>

                {/* 🔥 BOOST BADGE */}
                {pick.is_boosted && (
                  <span className="bg-yellow-400 text-black px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider shadow-sm animate-pulse">
                    🔥 Boosted (2x)
                  </span>
                )}
              </div>

              <p className="text-gray-900 font-black text-2xl mb-1">
                {pick.selection}
              </p>

              {/* GAME INFO SUB-TEXT */}
<div className="text-sm text-gray-500 mb-3">
  {pick.pick_type === "team_winner" ? (
    <>
      <p className="whitespace-nowrap"><span className="font-medium">Game:</span>{' '}{pick.game?.home_team} @ {pick.game?.away_team}</p>
      <p>{pick.game?.sport}</p>
    </>
  ) : (
    <>
      {pick.game && (
        <>
          <p className="whitespace-nowrap"><span className="font-medium">Game:</span>{' '}{pick.game.away_team} @ {pick.game.home_team}</p>
          <p>{pick.player?.sport}</p>
        </>
      )}
    </>
  )}
</div>
            </div>

            <div className="ml-4">
              {pick.status === "pending" && pick.locked && (
                <span className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-xl text-sm font-bold">
                  🔒 Locked
                </span>
              )}
              {pick.status !== "pending" && pick.status !== null && (
                <div className="text-right">
                  <div
                    className={`inline-block px-4 py-2 rounded-xl text-sm font-bold ${
                      parseInt(pick.fantasy_score) > 0
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {parseInt(pick.fantasy_score) > 0 ? "✓" : "✗"}{" "}
                    {pick.fantasy_score} pts
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
{myPicks.length === 0 && (
      <div className="text-center py-20 bg-gray-50 rounded-3xl border-4 border-dashed border-gray-200">
        <p className="text-5xl mb-4">🎯</p>
        <h3 className="text-xl font-black text-gray-900">No picks made yet!</h3>
        <p className="text-gray-500 mb-6">Head over to the games tab to start your lineup.</p>
      </div>
    )}
  </div>
)}    
{currentTab === "matchup" && (
  <div className="animate-slide-in">
    {!userMatchup ? (
      // No matchup for this week
      <div className="text-center py-20 bg-gray-50 rounded-3xl border-4 border-dashed border-gray-200">
        <p className="text-5xl mb-4">🤷</p>
        <h3 className="text-xl font-black text-gray-900">No Matchup This Week</h3>
        <p className="text-gray-500">You might have a bye week, or matchups haven't been generated yet.</p>
      </div>
    ) : (
      <div className="space-y-6">
        {/* Week Selector (same as My Picks tab) */}
        <div className="flex items-center gap-4 mb-6 bg-white p-2 rounded-2xl w-fit border-2 border-gray-100">
<button 
  onClick={() => {
    const prev = Math.max(1, viewingWeek - 1);
    setViewingWeek(prev);
    loadSeasonData(prev);
  }}
  disabled={viewingWeek === 1}
  className="p-2 hover:bg-gray-100 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed"
>
  ⬅️
</button>

<span className="font-black text-md">Week {viewingWeek} <span className="text-xs text-gray-400 font-normal">/ {totalWeeks}</span></span>

<button 
  onClick={() => {
    const next = Math.min(totalWeeks, viewingWeek + 1);
    setViewingWeek(next);
    loadSeasonData(next);
  }}
  disabled={viewingWeek === totalWeeks}
  className="p-2 hover:bg-gray-100 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed"
>
  ➡️
</button>
        </div>

        {/* Matchup Header - VS Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-3xl p-8 shadow-2xl">
          <p className="text-center text-emerald-400 text-xs font-black uppercase tracking-widest mb-6">
            Week {userMatchup.weekNumber} Head-to-Head
          </p>
          
          <div className="grid grid-cols-3 items-center gap-6">
            {/* You */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-emerald-500 rounded-full flex items-center justify-center text-3xl font-black mb-3">
                {currentUser.username.charAt(0).toUpperCase()}
              </div>
              <p className="font-black text-lg mb-2">{currentUser.username}</p>
              <div className={`text-5xl font-black ${
                userMatchup.myScore > userMatchup.opponentScore && userMatchup.isCompleted
                  ? 'text-emerald-400' 
                  : userMatchup.myScore < userMatchup.opponentScore && userMatchup.isCompleted
                  ? 'text-red-400'
                  : 'text-white'
              }`}>
                {userMatchup.myScore}
              </div>
            </div>

            {/* VS */}
<div className="text-center self-start pt-4">
  <div className="bg-white/10 backdrop-blur rounded-2xl px-4 py-2">
    <p className="text-2xl font-black">VS</p>
  </div>
</div>

            {/* Opponent */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-red-500 rounded-full flex items-center justify-center text-3xl font-black mb-3">
                {userMatchup.opponentName.charAt(0).toUpperCase()}
              </div>
              <p className="font-black text-lg mb-2">{userMatchup.opponentName}</p>
              <div className={`text-5xl font-black ${
                userMatchup.opponentScore > userMatchup.myScore && userMatchup.isCompleted
                  ? 'text-emerald-400' 
                  : userMatchup.opponentScore < userMatchup.myScore && userMatchup.isCompleted
                  ? 'text-red-400'
                  : 'text-white'
              }`}>
                {userMatchup.opponentScore}
              </div>
            </div>
          </div>

          {/* Result Banner */}
          {userMatchup.isCompleted && (
            <div className={`mt-6 text-center py-3 rounded-xl font-black text-lg ${
              userMatchup.winnerId === currentUser.id
                ? 'bg-emerald-500 text-white'
                : userMatchup.winnerId === userMatchup.opponentId
                ? 'bg-red-500 text-white'
                : 'bg-yellow-500 text-black'
            }`}>
              {userMatchup.winnerId === currentUser.id 
                ? '🎉 YOU WIN!' 
                : userMatchup.winnerId === userMatchup.opponentId
                ? '😔 You Lost'
                : '🤝 TIE GAME'
              }
            </div>
          )}
        </div>

        {/* Side-by-Side Pick Comparison */}
        <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-gray-100">
          <h3 className="text-2xl font-black text-gray-900 mb-6">Pick Breakdown</h3>
          
          <div className="space-y-4">
            {/* Compare picks by type */}
            {['team_winner', 'top_performer'].map(pickType => {
              const myTypePicks = userMatchup.myPicks.filter(p => p.pick_type === pickType);
              const oppTypePicks = userMatchup.opponentPicks.filter(p => p.pick_type === pickType);
              const maxLength = Math.max(myTypePicks.length, oppTypePicks.length);

              return (
                <div key={pickType} className="mb-8">
                  <h4 className="text-sm font-black uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
                    {pickType === 'team_winner' ? '🏆 Team Winners' : '⭐ Top Performers'}
                  </h4>
                  
                  {Array.from({ length: maxLength }).map((_, idx) => {
                    const myPick = myTypePicks[idx];
                    const oppPick = oppTypePicks[idx];

                    return (
                      <div key={idx} className="grid grid-cols-2 gap-4 mb-3">
                        {/* Your Pick */}
                        <div className={`p-4 rounded-xl border-2 ${
                          myPick 
                            ? myPick.status === 'graded'
                              ? parseInt(myPick.fantasy_score) > 0
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                              : 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          {myPick ? (
                            <>
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-black text-gray-900">{myPick.selection}</p>
                                {myPick.is_boosted && (
                                  <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded font-black">
                                    🔥 2x
                                  </span>
                                )}
                              </div>
                              {myPick.status === 'graded' && (
                                <p className={`text-sm font-bold ${
                                  parseInt(myPick.fantasy_score) > 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {myPick.fantasy_score} pts
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-gray-400 text-sm">No pick</p>
                          )}
                        </div>

                        {/* Opponent's Pick */}
                        <div className={`p-4 rounded-xl border-2 ${
                          oppPick 
                            ? oppPick.status === 'graded'
                              ? parseInt(oppPick.fantasy_score) > 0
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                              : 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          {oppPick ? (
                            <>
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-black text-gray-900">{oppPick.selection}</p>
                                {oppPick.is_boosted && (
                                  <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded font-black">
                                    🔥 2x
                                  </span>
                                )}
                              </div>
                              {oppPick.status === 'graded' && (
                                <p className={`text-sm font-bold ${
                                  parseInt(oppPick.fantasy_score) > 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {oppPick.fantasy_score} pts
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-gray-400 text-sm">No pick</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Stats Comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 text-center">
            <p className="text-xs font-black uppercase text-emerald-600 mb-1">Your Accuracy</p>
            <p className="text-3xl font-black text-emerald-700">
              {userMatchup.myPicks.filter(p => p.status === 'graded' && parseInt(p.fantasy_score) > 0).length} / {userMatchup.myPicks.filter(p => p.status === 'graded').length}
            </p>
            <p className="text-xs text-emerald-600 mt-1">picks correct</p>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-center">
            <p className="text-xs font-black uppercase text-red-600 mb-1">Opponent Accuracy</p>
            <p className="text-3xl font-black text-red-700">
              {userMatchup.opponentPicks.filter(p => p.status === 'graded' && parseInt(p.fantasy_score) > 0).length} / {userMatchup.opponentPicks.filter(p => p.status === 'graded').length}
            </p>
            <p className="text-xs text-red-600 mt-1">picks correct</p>
          </div>
        </div>
      </div>
    )}
  </div>
)}
        {/* LEADERBOARD TAB */}
        {currentTab === "leaderboard" && (
          <div className="animate-slide-in">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Rankings
              </h2>
              <p className="text-gray-500">
                See how you stack up against the competition
              </p>
            </div>

            {/* <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
              {[
                { id: "combined", label: "Combined" },
                { id: "hitrate", label: "Hit Rate" },
                { id: "roi", label: "ROI" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setLeaderboardTab(tab.id)}
                  className={`px-6 py-3 rounded-xl font-bold whitespace-nowrap transition shadow-md ${
                    leaderboardTab === tab.id
                      ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white scale-105"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div> */}

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Score
                      </th>
                      {/* <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Win %
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        ROI
                      </th> */}
                      {/* <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Bankroll
                      </th> */}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leaderboard.map((entry) => (
                      <tr
                        key={entry.rank}
                        className={`transition ${
                          entry.username === "You"
                            ? "bg-gradient-to-r from-emerald-50 to-green-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {entry.rank === 1 && (
                              <span className="text-3xl">🥇</span>
                            )}
                            {entry.rank === 2 && (
                              <span className="text-3xl">🥈</span>
                            )}
                            {entry.rank === 3 && (
                              <span className="text-3xl">🥉</span>
                            )}
                            {entry.rank > 3 && (
                              <span className="text-gray-600 font-bold text-lg">
                                #{entry.rank}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-bold text-lg ${
                                entry.username === "You"
                                  ? "text-emerald-600"
                                  : "text-gray-900"
                              }`}
                            >
                              {entry.username}
                            </span>
                            {entry.trend === "up" && (
                              <span className="text-green-500">↑</span>
                            )}
                            {entry.trend === "down" && (
                              <span className="text-red-500">↓</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-600">
                            {entry.fantasy_score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MANAGE TAB */}
{currentTab === "manage" && (
  <div className="animate-slide-in">

    {/* COMMISSIONER VIEW */}
    {isCommissioner ? (
      <>
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Manage Season</h2>
          <p className="text-gray-500">Commissioner controls</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-4 mb-6">
          <p className="font-bold text-emerald-900">👑 You are the commissioner</p>
          <p className="text-sm text-emerald-700">You can add games, grade picks, and manage season settings</p>
        </div>

        <div className="grid gap-5 mb-8">
          <button
            onClick={() => setShowAddGameModal(true)}
            className="bg-white rounded-2xl shadow-lg p-7 text-left card-hover border border-gray-100"
          >
            <div className="flex items-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-600 to-green-600 flex items-center justify-center text-2xl mr-5 shadow-lg">➕</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-1">Add Game</h3>
                <p className="text-sm text-gray-500">Add a new game for members to pick</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setShowAddPlayersModal(true)}
            className="bg-white rounded-2xl shadow-lg p-7 text-left card-hover border border-gray-100"
          >
            <div className="flex items-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-2xl mr-5 shadow-lg">⭐</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-1">Add Featured Players</h3>
                <p className="text-sm text-gray-500">Add players to existing games for performance picks</p>
              </div>
            </div>
          </button>

          <button
            onClick={async () => {
              await refreshPicksToGrade();
              setShowGradePicksModal(true);
            }}
            className="bg-white rounded-2xl shadow-lg p-7 text-left card-hover border border-gray-100"
          >
            <div className="flex items-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl mr-5 shadow-lg">✅</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-1">Grade Picks</h3>
                <p className="text-sm text-gray-500">Mark picks as win/loss after games end</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-white rounded-2xl shadow-lg p-7 text-left card-hover border border-gray-100"
          >
            <div className="flex items-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl mr-5 shadow-lg">👥</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-1">Invite Members</h3>
                <p className="text-sm text-gray-500">Share invite link with friends</p>
              </div>
            </div>
          </button>

          {allMatchups.length === 0 && (
            <div className="bg-emerald-900 p-6 rounded-2xl text-white">
              <h3 className="text-xl font-bold mb-2">Initialize PickSlate Schedule</h3>
              <p className="text-emerald-200 text-sm mb-4">
                Ready to start the season? This will pair all current members into weekly H2H matchups.
              </p>
              <button
                onClick={async () => {
                  if (window.confirm("Generate schedule for the whole season?")) {
                    await generateSeasonSchedule(selectedSeason.id);
                    await loadSeasonData();
                  }
                }}
                className="bg-white text-emerald-900 px-6 py-2 rounded-lg font-bold hover:bg-emerald-50 transition"
              >
                Generate H2H Matchups
              </button>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-emerald-600 to-green-600 text-white rounded-2xl p-7 shadow-xl">
          <h3 className="font-bold text-xl mb-4">Season Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Duration</p>
              <p className="text-white font-bold">{formatETDate(selectedSeason.start_date)} - {formatETDate(selectedSeason.end_date)}</p>
            </div>
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Scoring</p>
              <p className="text-white font-bold">Combined</p>
            </div>
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Pick Limit</p>
              <p className="text-white font-bold">5 per week</p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-emerald-100 text-xs font-semibold mb-2">Sports</p>
            <div className="flex gap-2 flex-wrap">
              {["NFL", "NBA", "NHL", "MLB", "Soccer", "NCAA FB", "NCAA BB"].map((sport) => (
                <span key={sport} className="bg-white/20 backdrop-blur px-3 py-1 rounded-lg text-sm font-bold">{sport}</span>
              ))}
            </div>
          </div>
        </div>
      </>

    ) : (
      /* MEMBER VIEW */
      <>
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Season Info</h2>
          <p className="text-gray-500">You are a member of this season</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-600 to-green-600 text-white rounded-2xl p-7 shadow-xl">
          <h3 className="font-bold text-xl mb-4">Season Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Commissioner</p>
              <p className="text-white font-bold">{selectedSeason?.commissioner}</p>
            </div>
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Duration</p>
              <p className="text-white font-bold">{formatETDate(selectedSeason.start_date)} - {formatETDate(selectedSeason.end_date)}</p>
            </div>
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Pick Limit</p>
              <p className="text-white font-bold">5 per week</p>
            </div>
            <div className="stat-card rounded-xl p-4">
              <p className="text-emerald-100 text-xs font-semibold mb-1">Members</p>
              <p className="text-white font-bold">{selectedSeason?.members}</p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-emerald-100 text-xs font-semibold mb-2">Sports</p>
            <div className="flex gap-2 flex-wrap">
              {["NFL", "NBA", "NHL", "MLB", "Soccer", "NCAA FB", "NCAA BB"].map((sport) => (
                <span key={sport} className="bg-white/20 backdrop-blur px-3 py-1 rounded-lg text-sm font-bold">{sport}</span>
              ))}
            </div>
          </div>
        </div>
      </>
    )}
  </div>
)}
      </div>

      {/* BOTTOM NAVIGATION */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50">
        <div className="max-w-7xl mx-auto px-2 py-3">
          <div className="flex justify-around">
            {[
              { id: "picks", label: "My Picks", icon: "📊" },
              { id: "matchup", label: "Matchup", icon: "⚔️" },
              { id: "leaderboard", label: "Rankings", icon: "🏆" },
              {
                id: "manage",
                label: isCommissioner ? "Manage" : "Info",
                icon: isCommissioner ? "⚙️" : "ℹ️",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(tab.id)}
                className={`flex flex-col items-center px-5 py-2 rounded-xl transition-all ${
                  currentTab === tab.id
                    ? "bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-700 scale-105"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <span className="text-2xl mb-0.5">{tab.icon}</span>
                <span className="text-xs font-semibold">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MAKE PICKS MODAL */}
{showMakePickModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-br from-emerald-600 to-green-600 text-white p-6 rounded-t-2xl z-20">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold">Make Your Picks</h2>
          <button 
            onClick={() => setShowMakePickModal(false)} 
            className="text-white hover:bg-white/20 rounded-lg p-2 transition"
          >
            ✕
          </button>
        </div>
        <p className="text-emerald-100 text-sm">
          Select 3 teams + 2 players. Locked games (🔒) cannot be changed.
        </p>
      </div>

      <form onSubmit={handleSubmitPicks} className="p-7 space-y-8 max-h-[calc(100vh-200px)] overflow-y-auto">
        
        {/* SECTION 1: Team Winners */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex justify-between items-center">
            🏆 Team Winners
            <span className="text-sm font-normal text-gray-500">{selections.teamWinners.length}/3</span>
          </h3>
          <div className="space-y-3">
            {upcomingGames.map((game) => {
              const isStarted = new Date() > new Date(game.game_time);
              return (
                <div key={game.id} className={`bg-gray-50 rounded-xl p-5 border-2 transition-all ${isStarted ? "opacity-60 grayscale-[0.5] border-gray-100" : "border-gray-200"}`}>
                  <div className="flex justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider">{game.sport} {isStarted && "• LOCKED"}</span>
                    <span className="text-xs text-gray-500 font-medium">{formatGameTime(game.game_time)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[game.away_team, game.home_team].map((team) => {
                      const isSelected = isTeamSelected(game.id, team);
                      const isBoosted = selections.boostedTeamId === `${game.id}-${team}`;
                      return (
                        <div key={team} className="relative group">
                          <button
                            type="button"
                            disabled={isStarted}
                            onClick={() => toggleTeamWinner(game.id, team, game.sport)}
                            className={`w-full py-3 px-4 rounded-xl font-bold transition-all ${
                              isSelected 
                                ? "bg-emerald-600 text-white shadow-lg ring-4 ring-emerald-100" 
                                : "bg-white border-2 border-gray-300 text-gray-900"
                            } ${isStarted ? "cursor-not-allowed" : "hover:border-emerald-400 active:scale-95"}`}
                          >
                            {isStarted && !isSelected ? "🔒 " : ""}{team}
                          </button>
                          
                          {/* Team Boost Button */}
                          {isSelected && (
                            <button
                              type="button"
                              disabled={isStarted}
                              onClick={(e) => {
                                e.stopPropagation();
                                if(!isStarted) setSelections(prev => ({ 
                                  ...prev, 
                                  boostedTeamId: isBoosted ? null : `${game.id}-${team}` 
                                }));
                              }}
                              className={`absolute -top-2 -right-2 px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all z-10 shadow-md ${
                                isBoosted 
                                  ? "bg-yellow-400 text-black border-2 border-white scale-110" 
                                  : "bg-gray-800 text-white opacity-50 hover:opacity-100"
                              } ${isStarted ? "cursor-not-allowed" : ""}`}
                            >
                              {isBoosted ? "🔥 Boosted" : "🔥 Boost?"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION 2: Top Performers */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex justify-between items-center">
            ⭐ Top Performers
            <span className="text-sm font-normal text-gray-500">{selections.topPerformers.length}/2</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {featuredPlayers.map((player) => {
              const isSelected = isPlayerSelected(player.id);
              const isBoosted = selections.boostedPlayerId === player.id;
              // Assuming player data has game_time from the joined query
              const isStarted = new Date() > new Date(player.game_time);
              const playerGameId = player.game_id || player.gameId;
              return (
                <div key={player.id} className="relative">
                  <button
                    type="button"
                    disabled={isStarted}
                    onClick={() => toggleTopPerformer(player.id, player.name, player.team, player.sport, playerGameId)}
                    className={`w-full p-4 rounded-xl font-bold text-left transition-all flex items-center justify-between ${
                      isSelected
                        ? "bg-purple-600 text-white shadow-lg ring-4 ring-purple-100"
                        : "bg-white border-2 border-gray-300 text-gray-900"
                    } ${isStarted ? "opacity-50 cursor-not-allowed grayscale" : "hover:border-purple-400 active:scale-95"}`}
                  >
                    <div>
                      <div className={`text-[10px] font-black uppercase mb-1 ${isSelected ? "text-purple-200" : "text-gray-400"}`}>
                        {player.sport} • {player.team} {isStarted && "• LOCKED"}
                      </div>
                      <div className="text-base leading-tight">{player.name}</div>
                    </div>
                    <div className="text-xl">{isStarted && !isSelected ? "🔒" : isSelected ? "✓" : "⭐"}</div>
                  </button>

                  {/* Player Boost Button */}
                  {isSelected && (
                    <button
                      type="button"
                      disabled={isStarted}
                      onClick={(e) => {
                        e.stopPropagation();
                        if(!isStarted) setSelections(prev => ({ 
                          ...prev, 
                          boostedPlayerId: isBoosted ? null : player.id 
                        }));
                      }}
                      className={`absolute -top-2 -right-2 px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all z-10 shadow-md ${
                        isBoosted 
                          ? "bg-yellow-400 text-black border-2 border-white scale-110" 
                          : "bg-gray-800 text-white opacity-50 hover:opacity-100"
                      } ${isStarted ? "cursor-not-allowed" : ""}`}
                    >
                      {isBoosted ? "🔥 Boosted" : "🔥 Boost?"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky Submit Footer */}
        <div className="sticky bottom-0 bg-white pt-6 border-t border-gray-100 pb-2">
          <button
            type="submit"
            disabled={!canSubmit()}
            className={`w-full py-5 rounded-xl font-bold text-lg shadow-xl transition-all active:scale-[0.98] ${
              canSubmit()
                ? "bg-gradient-to-br from-emerald-600 to-green-600 text-white hover:shadow-2xl"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {canSubmit() ? "🔒 Save Changes" : "Complete Your Lineup"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      {/* ADD GAME MODAL */}
      {showAddGameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-br from-emerald-600 to-green-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Add Game</h2>
                <button
                  onClick={() => setShowAddGameModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                >
                  ✕
                </button>
              </div>
              <p className="text-emerald-100 text-sm mt-1">
                Add a game for members to pick
              </p>
            </div>

            <form onSubmit={handleAddGame} className="p-7 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Sport
                </label>
                <select
                  value={addGameData.sport}
                  onChange={(e) =>
                    setAddGameData({ ...addGameData, sport: e.target.value })
                  }
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-base font-medium outline-none"
                >
                  <option value="NFL">NFL</option>
                  <option value="NBA">NBA</option>
                  <option value="NHL">NHL</option>
                  <option value="MLB">MLB</option>
                  <option value="Soccer">Soccer</option>
                  <option value="NCAA FB">NCAA FB</option>
                  <option value="NCAA BB">NCAA BB</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Home Team
                </label>
                <input
                  type="text"
                  placeholder="e.g., Kansas City Chiefs"
                  value={addGameData.home_team}
                  onChange={(e) =>
                    setAddGameData({
                      ...addGameData,
                      home_team: e.target.value,
                    })
                  }
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-lg outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Away Team
                </label>
                <input
                  type="text"
                  placeholder="e.g., Baltimore Ravens"
                  value={addGameData.away_team}
                  onChange={(e) =>
                    setAddGameData({
                      ...addGameData,
                      away_team: e.target.value,
                    })
                  }
                  required
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 transition text-lg outline-none"
                />
              </div>

<div className="grid grid-cols-2 gap-5">
  <div>
    <label className="block text-sm font-bold text-gray-900 mb-3">
      Game Date
    </label>
<input
  type="date"
  value={addGameData.gameDate}
  onChange={(e) => setAddGameData({ ...addGameData, gameDate: e.target.value })}
  required
  className="max-w-[200px] min-w-[200px] py-3 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-base"
/>
  </div>
  <div>
    <label className="block text-sm font-bold text-gray-900 mb-3">
      Game Time
    </label>
<input
  type="time"
  value={addGameData.gameTime}
  onChange={(e) => setAddGameData({ ...addGameData, gameTime: e.target.value })}
  required
  className="max-w-[200px] min-w-[200px] py-3 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-base"
/>
  </div>
</div>

              <button
                type="submit"
                className="w-full btn-primary text-white py-5 rounded-xl font-bold text-lg shadow-xl"
              >
                Add Game to Season
              </button>
            </form>
          </div>
        </div>
      )}

      {/* GRADE PICKS & INVITE MODALS kept the same */}
{showGradePicksModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      
      {/* Sticky Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-green-600 text-white p-6 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Grade Weekly Outcomes</h2>
            <p className="text-emerald-100 text-sm mt-1">
              Grading a selection updates every user who picked it.
            </p>
          </div>
          <button
            onClick={() => setShowGradePicksModal(false)}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition w-10 h-10 flex items-center justify-center"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="p-7 overflow-y-auto">
        {picksToGrade.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No picks to grade</h3>
            <p className="text-gray-600">All outcomes have been settled for this week!</p>
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Commissioner Note:</strong> You are grading unique selections. 
                Assigning a score to "Lions" or "LeBron James" will apply to <strong>all</strong> users who made that pick.
              </p>
            </div>

            <div className="space-y-4">
{picksToGrade.map((item) => (
  <div
    key={item.selection}
    className="bg-gray-50 rounded-xl p-5 border-2 border-gray-200 hover:border-emerald-200 transition-colors"
  >
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
            item.pickType === "team_winner" 
              ? "bg-blue-100 text-blue-700" 
              : "bg-purple-100 text-purple-700"
          }`}>
            {item.pickType === "team_winner" ? "Team" : "Player"}
          </span>
          {/* Show count of how many users have this pick */}
          <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold">
            {item.count} {item.count === 1 ? 'User' : 'Users'}
          </span>
        </div>
        
        <p className="text-xl font-black text-gray-900 leading-tight">
          {item.selection}
        </p>

        {/* Show matchup info from the game object */}
        {item.game && (
          <p className="text-sm text-gray-500 font-medium">
            {item.game.away_team} @ {item.game.home_team} • {item.game.sport} • {new Date(item.game.game_time).toLocaleString("en-US", { 
              timeZone: "America/New_York",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "numeric"
            })}
          </p>
        )}
      </div>


                    <div className="sm:min-w-[220px]">
                      <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">
                        Outcome Score
                      </label>
                      <select
                        // CHANGE: Call handleGradeSelection (Bulk) instead of handleGradePick (Single)
                        onChange={(e) => handleGradeSelection(item.selection, e.target.value)}
                        defaultValue=""
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-bold text-gray-900 outline-none bg-white cursor-pointer"
                      >
                        <option value="" disabled>Select points...</option>
                        <option value="0">0</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                        <option value="300">300</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowGradePicksModal(false)}
              className="w-full mt-8 bg-gray-900 text-white py-4 rounded-xl font-bold hover:bg-black transition shadow-lg"
            >
              Done Grading
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}
{showHelpModal && (
  <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
    <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
      
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6 rounded-t-3xl sm:rounded-t-3xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-black text-xl">
              {isCommissioner ? '👑 Commissioner Guide' : '📖 How to Play'}
            </p>
            <p className="text-slate-300 text-sm mt-0.5">
              {isCommissioner ? 'Your weekly checklist' : 'PickSlate in 60 seconds'}
            </p>
          </div>
          <button
            onClick={() => setShowHelpModal(false)}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 transition flex items-center justify-center font-black"
          >
            ✕
          </button>
        </div>

        {/* Tab switcher — only show if commissioner (so they can see both) */}
        {isCommissioner && (
          <div className="flex gap-2 mt-4">
            {['commissioner', 'player'].map((t) => (
              <button
                key={t}
                onClick={() => setHelpTab(t)}
                className={`px-4 py-1.5 rounded-xl text-sm font-bold transition ${
                  helpTab === t ? 'bg-white text-slate-900' : 'bg-white/10 text-white'
                }`}
              >
                {t === 'commissioner' ? '👑 Commissioner' : '🎯 Player'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="divide-y divide-gray-100">

        {/* COMMISSIONER TAB */}
        {isCommissioner && helpTab === 'commissioner' && (
          <>
            {/* Weekly checklist */}
            <div className="p-5">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Weekly Checklist</p>
              <div className="space-y-3">
                {[
                  {
                    step: '1',
                    icon: '➕',
                    color: 'bg-emerald-100 text-emerald-700',
                    title: 'Add this week\'s games',
                    sub: 'Go to Manage → Add Game. Pick the sport, teams, date and time. Games must be added before players can make picks.',
                  },
                  {
                    step: '2',
                    icon: '⭐',
                    color: 'bg-purple-100 text-purple-700',
                    title: 'Add featured players',
                    sub: 'Go to Manage → Add Featured Players. Select a game, then add 2–4 players members can pick as top performers.',
                  },
                  {
                    step: '3',
                    icon: '⏳',
                    color: 'bg-orange-100 text-orange-700',
                    title: 'Wait for games to finish',
                    sub: 'Picks lock automatically at kickoff. You don\'t need to do anything — just let the games play out.',
                  },
                  {
                    step: '4',
                    icon: '✅',
                    color: 'bg-blue-100 text-blue-700',
                    title: 'Grade the picks',
                    sub: 'Go to Manage → Grade Picks. You\'ll see every unique selection. Assign points (0 = wrong, 100+ = correct). Grading one selection updates all users who picked it.',
                  },
                  {
                    step: '5',
                    icon: '🔄',
                    color: 'bg-gray-100 text-gray-700',
                    title: 'Repeat next week',
                    sub: 'Add new games for the next week. The schedule and matchups were generated at season start — you just keep the slate fresh.',
                  },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full ${item.color} flex items-center justify-center font-black text-sm shrink-0 mt-0.5`}>
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-sm">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Scoring guide */}
            <div className="p-5">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Scoring Guide</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { pts: '0', label: 'Wrong pick', color: 'bg-red-50 border-red-200 text-red-700' },
                  { pts: '100', label: 'Correct pick', color: 'bg-green-50 border-green-200 text-green-700' },
                  { pts: '200+', label: 'Boosted + correct', color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                ].map((s) => (
                  <div key={s.pts} className={`border-2 rounded-xl p-3 text-center ${s.color}`}>
                    <p className="font-black text-lg">{s.pts}</p>
                    <p className="text-xs font-medium mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Boosted picks are worth 2x whatever score you assign. A boosted correct pick set to 100 = 200 pts.</p>
            </div>

            {/* Tips */}
            <div className="p-5 pb-8">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Tips</p>
              <div className="space-y-2">
                {[
                  '📅 Add games early — members can\'t pick until you do',
                  '⭐ Add at least 4 featured players per week for variety',
                  '✅ Grade picks within 24hrs of games ending',
                  '👥 Share your invite code early so everyone\'s in before Week 1',
                ].map((tip, i) => (
                  <p key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span>{tip}</span>
                  </p>
                ))}
              </div>
            </div>
          </>
        )}

        {/* HOW TO PLAY TAB (members always see this, commissioners see it on the player tab) */}
        {(!isCommissioner || helpTab === 'player') && (
          <>
            {[
              { step: '1', icon: '📅', title: 'Your commissioner sets the weekly slate', sub: 'Games and featured players are added each week for you to pick from' },
              { step: '2', icon: '🎯', title: 'Make 5 picks before game time', sub: '3 team winners + 2 top performers. Picks lock the moment a game starts' },
              { step: '3', icon: '🔥', title: 'Boost one pick for 2x points', sub: 'Feeling confident? Boost a pick. High risk, high reward — use it wisely' },
              { step: '4', icon: '⚔️', title: 'Battle your weekly opponent H2H', sub: 'You go head-to-head against one player each week in the Matchup tab' },
              { step: '5', icon: '🏆', title: 'Best score at season end wins', sub: 'Most total points takes the championship' },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-black text-sm shrink-0 mt-0.5">
                  {item.step}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span>{item.icon}</span>
                    <p className="font-black text-gray-900 text-sm">{item.title}</p>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.sub}</p>
                </div>
              </div>
            ))}
            <div className="p-5 pb-8 bg-emerald-50">
              <p className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-2">Scoring</p>
              <p className="text-sm text-emerald-800">Correct team pick = <strong>100 pts</strong> · Correct player pick = varies · Boosted = <strong>2x points</strong> · Win your H2H matchup = <strong>+100 pts</strong></p>
            </div>
          </>
        )}

      </div>
    </div>
  </div>
)}
{showInviteModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
      <div className="bg-gradient-to-br from-emerald-600 to-green-600 text-white p-6 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Invite Members</h2>
          <button
            onClick={() => setShowInviteModal(false)}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="p-7">
        <p className="text-gray-600 mb-4">
          Share this invite code with friends:
        </p>

        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-6 mb-6">
          <p className="text-xs font-bold text-emerald-700 mb-2">
            INVITE CODE
          </p>
          <p className="text-4xl font-bold text-emerald-900 tracking-wider mb-4">
            {selectedSeason?.invite_code || 'LOADING...'}
          </p>
          <button 
            onClick={() => {
              navigator.clipboard.writeText(selectedSeason?.invite_code || '');
              alert('✅ Invite code copied to clipboard!');
            }}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-bold transition"
          >
            📋 Copy Code
          </button>
        </div>

        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-sm font-bold text-gray-900 mb-2">
            How to share:
          </p>
          <ol className="text-sm text-gray-600 space-y-1 ml-4 list-decimal">
            <li>Copy the invite code above</li>
            <li>Share it with your friends (text, email, etc.)</li>
            <li>They click "Join Season" and enter the code</li>
            <li>They're in! 🎉</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
)}
{showAddPlayersModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-gradient-to-br from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Add Featured Players</h2>
          <button
            onClick={() => setShowAddPlayersModal(false)}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition"
          >
            ✕
          </button>
        </div>
        <p className="text-purple-100 text-sm mt-1">
          Add players to a game for members to pick as top performers
        </p>
      </div>

      <form onSubmit={handleAddFeaturedPlayers} className="p-7 space-y-6">
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-3">
            Select Game
          </label>
          <select
            value={addPlayersData.gameId}
            onChange={(e) =>
              setAddPlayersData({ ...addPlayersData, gameId: e.target.value })
            }
            required
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition text-base font-medium outline-none"
          >
            <option value="">-- Select a game --</option>
            {games.map((game) => (
              <option key={game.id} value={game.id}>
                {game.sport}: {game.away_team} @ {game.home_team} - {formatGameTime(game.game_time)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-bold text-gray-900">
              Featured Players
            </label>
            <button
              type="button"
              onClick={addPlayerRow}
              className="text-purple-600 hover:text-purple-700 font-bold text-sm flex items-center gap-1"
            >
              <span className="text-lg">+</span> Add Another Player
            </button>
          </div>

          <div className="space-y-4">
            {addPlayersData.players.map((player, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <p className="font-bold text-gray-700 text-sm">
                    Player {index + 1}
                  </p>
                  {addPlayersData.players.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePlayerRow(index)}
                      className="text-red-600 hover:text-red-700 text-sm font-bold"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">
                      Player Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Patrick Mahomes"
                      value={player.name}
                      onChange={(e) =>
                        updatePlayer(index, "name", e.target.value)
                      }
                      required
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 transition outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">
                      Team *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Chiefs"
                      value={player.team}
                      onChange={(e) =>
                        updatePlayer(index, "team", e.target.value)
                      }
                      required
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 transition outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">
                      Position
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., QB"
                      value={player.position}
                      onChange={(e) =>
                        updatePlayer(index, "position", e.target.value)
                      }
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 transition outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
          <p className="text-sm text-purple-800">
            💡 <strong>Tip:</strong> Add 2-4 players per game to give members good options for top performer picks.
          </p>
        </div>

        <button
          type="submit"
          className="w-full bg-gradient-to-br from-purple-600 to-pink-600 text-white py-5 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition"
        >
          Add Featured Players
        </button>
      </form>
    </div>
  </div>
)}

    </div>
  );
}
